'use client';

import { motion } from 'framer-motion';
import { 
  Server, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Wifi,
  TrendingUp,
  TrendingDown,
  Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface StatsCardProps {
  title: string;
  value: number | string;
  change?: number;
  changeLabel?: string;
  icon: 'total' | 'online' | 'warning' | 'offline' | 'bandwidth';
  trend?: 'up' | 'down' | 'neutral';
}

const iconConfig = {
  total: { icon: Server, bgColor: 'bg-[var(--primary)]/10', iconColor: 'text-[var(--primary)]' },
  online: { icon: CheckCircle2, bgColor: 'bg-[var(--success)]/10', iconColor: 'text-[var(--success)]' },
  warning: { icon: AlertTriangle, bgColor: 'bg-[var(--warning)]/10', iconColor: 'text-[var(--warning)]' },
  offline: { icon: XCircle, bgColor: 'bg-[var(--destructive)]/10', iconColor: 'text-[var(--destructive)]' },
  bandwidth: { icon: Wifi, bgColor: 'bg-[var(--info)]/10', iconColor: 'text-[var(--info)]' },
};

export default function StatsCard({ 
  title, 
  value, 
  change, 
  changeLabel = '较上周',
  icon,
  trend = 'neutral'
}: StatsCardProps) {
  const config = iconConfig[icon];
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-xl border border-[var(--border)] p-5 cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[var(--muted-foreground)] mb-1">{title}</p>
          <h3 className="text-2xl font-semibold text-[var(--foreground)]">{value}</h3>
          
          {change !== undefined && (
            <div className="flex items-center gap-1.5 mt-2">
              <div className={cn(
                "flex items-center gap-0.5 text-xs font-medium",
                trend === 'up' && "text-[var(--success)]",
                trend === 'down' && "text-[var(--destructive)]",
                trend === 'neutral' && "text-[var(--muted-foreground)]"
              )}>
                {trend === 'up' && <TrendingUp className="w-3.5 h-3.5" />}
                {trend === 'down' && <TrendingDown className="w-3.5 h-3.5" />}
                {trend === 'neutral' && <Activity className="w-3.5 h-3.5" />}
                <span>{change > 0 ? '+' : ''}{change}%</span>
              </div>
              <span className="text-xs text-[var(--muted-foreground)]">{changeLabel}</span>
            </div>
          )}
        </div>
        
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center",
          config.bgColor
        )}>
          <Icon className={cn("w-6 h-6", config.iconColor)} />
        </div>
      </div>

      {/* 进度条装饰 */}
      {icon === 'bandwidth' && (
        <div className="mt-4">
          <div className="h-1.5 bg-[var(--secondary)] rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: '67%' }}
              transition={{ duration: 1, delay: 0.3 }}
              className="h-full bg-gradient-to-r from-[var(--primary)] to-[var(--info)] rounded-full"
            />
          </div>
          <p className="text-xs text-[var(--muted-foreground)] mt-1.5">当前负载 67%</p>
        </div>
      )}

      {/* 在线率指示 */}
      {icon === 'online' && (
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-[var(--secondary)] rounded-full overflow-hidden flex">
            <div className="w-[92%] h-full bg-[var(--success)] rounded-full" />
          </div>
          <span className="text-xs font-medium text-[var(--success)]">92%</span>
        </div>
      )}
    </motion.div>
  );
}

// 统计卡片组
export function StatsGroup() {
  const stats = [
    { title: '设备总数', value: 156, change: 12, icon: 'total' as const, trend: 'up' as const },
    { title: '在线设备', value: 143, change: 5, icon: 'online' as const, trend: 'up' as const },
    { title: '告警设备', value: 8, change: -2, icon: 'warning' as const, trend: 'down' as const },
    { title: '离线设备', value: 5, change: -3, icon: 'offline' as const, trend: 'down' as const },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
        >
          <StatsCard {...stat} />
        </motion.div>
      ))}
    </div>
  );
}
